import TextInputField from 'components/TextInputField';
import './JoinBetaTab.css';
import ActionButton from 'components/ActionButton';
import { Controller, useForm } from 'react-hook-form';
import { LOGIN_EMAIL_PLACEHOLDER } from '../../constants';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useEffect } from 'react';
import { AssetUrls } from 'config/assets';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useCheckEmailExistsQuery } from 'store/services/auth/authApi';

const JOIN_BETA_TITLE = 'Join TradeMagic';
const JOIN_BETA_DESCRIPTION = 'Start by entering your email address.';
const JOIN_BETA_BUTTON = 'Create Account';

export const loginValidationRules = {
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[\w+.-]+@([\w-]+\.)+[\w-]{2,4}$/,
      message: 'Please enter a valid email address',
    },
  },
};

interface JoinBetaTabProps {
  onSwitchToSignIn?: (email: string) => void;
}

const JoinBetaTab = ({ onSwitchToSignIn }: JoinBetaTabProps) => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: { email: '' } });

  const emailValue = watch('email');
  const debouncedEmail = useDebouncedValue(emailValue, 400);
  const isWellFormed = loginValidationRules.email.pattern.value.test(debouncedEmail);

  const { data: emailCheck } = useCheckEmailExistsQuery(debouncedEmail, {
    skip: !isWellFormed,
  });
  const emailIsRegistered = isWellFormed && emailCheck?.exists === true;

  // Show toast notification when there's a validation error
  useEffect(() => {
    if (errors.email) {
      toast.error(errors.email.message || 'Please enter a valid email address');
    }
  }, [errors.email]);

  const onSubmit = (data: { email: string }) => {
    // Navigate to onboarding with the email as a query parameter
    navigate(`/onboarding?email=${encodeURIComponent(data.email)}`);
  };

  const handleSwitchToSignIn = () => {
    onSwitchToSignIn?.(getValues('email'));
  };

  return (
    <div className="join-beta">
      <div className="join-beta-messages">
        <div className="join-beta-title">
          {JOIN_BETA_TITLE}
        </div>
        <div className="join-beta-description">
          {JOIN_BETA_DESCRIPTION}
        </div>
      </div>
      <div className="join-beta-input">
        <Controller
          name="email"
          control={control}
          rules={loginValidationRules.email}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_EMAIL_PLACEHOLDER}
              error={Boolean(errors.email)}
              onChange={field.onChange}
            />
          )}
        />
        {emailIsRegistered && (
          <p className="join-beta-email-exists-prompt">
            Looks like you already have an account.{' '}
            <button type="button" onClick={handleSwitchToSignIn}>
              Switch to Sign In?
            </button>
          </p>
        )}
        <ActionButton
          label={JOIN_BETA_BUTTON}
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="trademagic-icon"
            />
          }
          onPress={handleSubmit(onSubmit)}
        />
        <p className="join-beta-helper-link">
          Already have an account?{' '}
          <button type="button" onClick={handleSwitchToSignIn}>
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};

export default JoinBetaTab;
